/**
 * The review queue — the session that replaces the copy-paste hour.
 *
 * New items, newest first. Everything she needs to decide is on the row: what it is, who
 * published it, what it says. Deciding is one click, or one click for a whole selection.
 * Nothing is deleted: Dismiss is a status, and dismissed items stay searchable.
 *
 * **Triage is one write.** Tagging a client, taking a suggested summary and picking a
 * category are all held here until she clicks Track or Dismiss, and then go up as a single
 * PATCH. Her first session made four separate writes on one item as she tried the client
 * picker on and off again, and each one earned its own audit line — a record of her
 * hesitating rather than of what she decided. A decision is the unit of work, so it is also
 * the unit of storage and the unit of audit.
 *
 * The Log view still saves each edit as it is made. Editing a filed record is a deliberate
 * act with no Track button to press, and there the audit line is the point.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { formatDate } from "../config";
import { DEFAULT_NEXT_STEP, dateFromNextStep } from "../nextstep";
import type { Client, Item, ItemPatch, PageUpdate } from "../types";
import type { AiVerdict } from "../types";
import {
  AI_VERDICT_LABELS,
  ALL_CLIENTS_TAG,
  aiVerdict,
  clientSuggestions,
  clientTags,
  coversLive,
  liveClients,
  toggleClient,
} from "../types";
import { ChangedPages, isStaleDecision } from "../components/ChangedPages";
import { ItemLink } from "../components/ItemLink";

/**
 * Tags read alphabetically wherever they are shown here.
 *
 * `tags.yaml` is in the order her export arrived in and stays that way — it sits in the
 * enrichment prompt's cached prefix, so reordering the file would invalidate that cache for
 * every item. Order is a reading question, so it is answered where they are read.
 */
const byName = (names: readonly string[]) =>
  [...names].sort((a, b) => a.localeCompare(b, "en-GB"));

interface Props {
  clients: Client[];
  onError: (error: unknown) => void;
  onCountChange: (count: number | null) => void;
}

export function ReviewQueue({ clients, onError, onCountChange }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [regulator, setRegulator] = useState("");
  const [verdict, setVerdict] = useState<AiVerdict | "">("");
  const [busy, setBusy] = useState(false);
  /** Edits she has made but not yet committed with Track or Dismiss, by item id. */
  const [pending, setPending] = useState<Record<string, ItemPatch>>({});
  const [vocabulary, setVocabulary] = useState<string[]>([]);
  /** Items in her log whose page its publisher has revised — see `ChangedPages`. */
  const [changed, setChanged] = useState<Item[]>([]);
  /** How many pages she has muted, for the band's footer. The list itself is on Sources. */
  const [mutedCount, setMutedCount] = useState(0);

  // Her tag vocabulary, loaded once. Failing to load it costs her the pickable list, not
  // the ability to triage, so it fails quietly to empty.
  useEffect(() => {
    api
      .listTags()
      .then((page) => setVocabulary(page.items))
      .catch(() => setVocabulary([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.listAllItems({ status: "new", regulator: regulator || undefined }),
      // A failure here costs her the band, not the queue: the new items are the session and
      // the changes are an addition to it.
      api
        .listChangedItems()
        .catch(() => ({ items: [] as Item[], count: 0, muted: [] as Item[], muted_count: 0 })),
    ])
      .then(([found, updates]) => {
        setItems(found);
        setChanged(updates.items);
        setMutedCount(updates.muted_count);
        // The badge counts everything waiting on this screen, not just the new items.
        // Counting only the new ones would leave a week with nothing new showing no badge
        // at all — and a queue with no badge is a queue she has no reason to open, which is
        // precisely the week a page that changed under her would go unseen.
        onCountChange(found.length + updates.items.length);
        setSelected(new Set());
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [regulator, onError, onCountChange]);

  useEffect(load, [load]);

  /**
   * Commit a decision on one item or a selection.
   *
   * `also` is anything the decision itself implies — today that is the next step
   * "Track + action" writes. It travels with the status rather than as a second call, so the
   * item is never briefly tracked-without-its-action and the audit still records one line.
   */
  const decide = async (
    ids: string[],
    status: "tracked" | "dismissed",
    also: ItemPatch = {},
  ) => {
    setBusy(true);
    try {
      if (ids.length === 1) {
        // Everything she changed on this item goes up with the decision, so the audit
        // records one line saying what she decided rather than several saying how she
        // got there.
        await api.patchItem(ids[0], { ...(pending[ids[0]] ?? {}), ...also, status });
      } else {
        // A bulk decision carries any edits made on the rows it covers, one call each for
        // those, then the status for all of them in one.
        const edited = ids.filter((id) => pending[id]);
        await Promise.all(edited.map((id) => api.patchItem(id, pending[id])));
        await api.bulkPatchAll(ids, { ...also, status });
      }
      setPending((current) => {
        const next = { ...current };
        ids.forEach((id) => delete next[id]);
        return next;
      });
      // Drop them from the list rather than reloading: she is mid-flow, and the scroll
      // position is hers.
      setItems((current) => current.filter((item) => !ids.includes(item.id)));
      setSelected(new Set());
      onCountChange(items.length - ids.length + changed.length);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Her decision on one revised page — update the log, update it with an action, or stop
   * tracking it. One POST, one audit line.
   *
   * The change date this card was showing travels with the decision, and the API refuses it if
   * the page has moved again since (or the other tab already decided). The refusal says so in a
   * sentence, and the band is reloaded behind it so the current state is already on screen.
   */
  const decideChange = async (item: Item, update: PageUpdate) => {
    try {
      await api.applyPageUpdate(item.id, update);
      setChanged((current) => {
        const next = current.filter((row) => row.id !== item.id);
        onCountChange(items.length + next.length);
        return next;
      });
    } catch (error) {
      if (isStaleDecision(error)) {
        loadChanges();
      }
      onError(error);
    }
  };

  /** The band on its own — for a summary that was still being written when the queue loaded. */
  const loadChanges = () => {
    api
      .listChangedItems()
      .then((updates) => {
        setChanged(updates.items);
        setMutedCount(updates.muted_count);
        onCountChange(items.length + updates.items.length);
      })
      .catch(onError);
  };

  /** Stage a change on a row. Nothing reaches the API until she decides. */
  const stage = (item: Item, patch: ItemPatch) => {
    setItems((current) =>
      current.map((row) => (row.id === item.id ? { ...row, ...patch } : row)),
    );
    setPending((current) => ({ ...current, [item.id]: { ...current[item.id], ...patch } }));
  };

  const tagClient = (item: Item, client: string) => {
    // Her shorthand is a master toggle here for the same reason it is one in the log's
    // picker: "All" means every client selected, so the chip has to read as on when they
    // are — otherwise the same fact looks like two different states on two screens.
    stage(item, { clients: toggleClient(item.clients, client, live) });
  };

  /**
   * Copy the drafted summary into her own Summary field.
   *
   * This is the step that carries it into the log and the Excel export, which read
   * `summary` and never `ai_summary` — deliberately, so nothing the model wrote can reach
   * a document she sends a client without her having accepted it first.
   */
  const useSummary = (item: Item) => {
    const next = item.ai_summary ?? "";
    if (next) stage(item, { summary: next });
  };

  /**
   * Track it, and put it on the Actions screen with a date still to give it.
   *
   * An action is its own record since 20 September 2026 (`reghorizon/actions.py`), so this is
   * two calls where it used to be one, and the **order** is what the old single call bought:
   * the action is written first and the item is tracked second. Tracked-without-its-action was
   * the state worth avoiding — a row on her log implying work that is on no screen — and
   * actioned-then-tracked is the safe direction to fail in. It is created with no client: this
   * button says "there is work here", not whose.
   *
   * Any step already on the item is kept; otherwise it gets a placeholder she can reword,
   * because a blank cell is not an action and the button would look like it had done nothing.
   *
   * A date is taken only from the *step itself*, by the same matcher the log uses — her words,
   * read the same way on both screens. It deliberately does not accept `ai_due_date`: a
   * suggested deadline is offered on the Actions screen, where the words it was read from are
   * shown beside it, and putting a date she has not seen onto a compliance log is exactly what
   * the `ai_*` fields exist to prevent. The placeholder names no date, so the ordinary case is
   * an action waiting for one.
   */
  const trackWithAction = async (item: Item) => {
    const step = (item.next_steps || "").trim() || DEFAULT_NEXT_STEP;
    const read = (item.due_date || "").trim() ? null : dateFromNextStep(step);
    try {
      await api.createAction(item.id, {
        next_steps: step,
        next_steps_status: "open",
        ...(read ? { due_date: read.date } : {}),
      });
    } catch (error) {
      onError(error);
      return;
    }
    decide([item.id], "tracked");
  };

  /** Take the confident suggestions in one click. Her existing tags are kept, not replaced. */
  const acceptSuggested = (item: Item, names: string[]) => {
    stage(item, { clients: [...new Set([...item.clients, ...names])] });
  };

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const regulators = [...new Set(items.map((item) => item.regulator))].sort();

  // Filtering is client-side because the queue is already loaded in full — she gets an
  // instant answer rather than a round trip per change of mind.
  const shown = verdict ? items.filter((item) => aiVerdict(item) === verdict) : items;
  const tally = items.reduce<Record<string, number>>((counts, item) => {
    const key = aiVerdict(item);
    counts[key] = (counts[key] ?? 0) + 1;
    return counts;
  }, {});
  const allShownSelected = shown.length > 0 && shown.every((item) => selected.has(item.id));
  const clientNames = clientTags(clients);
  const live = liveClients(clients);

  if (loading) return <div className="centred muted">Loading the queue…</div>;

  if (!items.length) {
    return (
      <div className="queue">
        <ChangedPages
          items={changed}
          mutedCount={mutedCount}
          onDecide={decideChange}
          onRefresh={loadChanges}
        />
        <div className="centred">
        <div className="empty">
          {/* "Nothing waiting" would contradict the band above it, which is waiting. */}
          <h2>{changed.length ? "No new items" : "Nothing waiting"}</h2>
          <p className="muted">
            {regulator
              ? `No new items from ${regulator}.`
              : "Every item has been reviewed. New ones arrive as the feeds run."}
          </p>
          {regulator && (
            <button className="link" onClick={() => setRegulator("")}>
              show all regulators
            </button>
          )}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="queue">
      <ChangedPages
        items={changed}
        mutedCount={mutedCount}
        onDecide={decideChange}
        onRefresh={loadChanges}
      />
      <div className="toolbar">
        <strong>
          {verdict
            ? `${shown.length} of ${items.length} shown`
            : `${items.length} item${items.length === 1 ? "" : "s"} awaiting review`}
        </strong>
        <select value={regulator} onChange={(event) => setRegulator(event.target.value)}>
          <option value="">All regulators</option>
          {regulators.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={verdict}
          onChange={(event) => setVerdict(event.target.value as AiVerdict | "")}
          title="Filter by what the assistant concluded"
        >
          <option value="">Any AI verdict</option>
          {(Object.keys(AI_VERDICT_LABELS) as AiVerdict[])
            .filter((key) => tally[key])
            .map((key) => (
              <option key={key} value={key}>
                {AI_VERDICT_LABELS[key]} ({tally[key]})
              </option>
            ))}
        </select>

        {shown.length > 0 && (
          <button
            onClick={() =>
              setSelected(allShownSelected ? new Set() : new Set(shown.map((i) => i.id)))
            }
          >
            {allShownSelected ? "Clear selection" : `Select all ${shown.length}`}
          </button>
        )}

        <span className="spacer" />
        {selected.size > 0 ? (
          // A group of its own so a phone can float it above the tab bar: with a selection
          // made further down the list, a button back at the top is a scroll away.
          <div className="bulk" role="group" aria-label="Decide the selection">
            <span className="muted">{selected.size} selected</span>
            <button
              className="primary"
              disabled={busy}
              onClick={() => decide([...selected], "tracked")}
            >
              Track selected
            </button>
            <button disabled={busy} onClick={() => decide([...selected], "dismissed")}>
              Dismiss selected
            </button>
          </div>
        ) : (
          <span className="muted small hint">
            <strong>Track</strong> adds it to the log · <strong>Track + action</strong> also
            puts it on Actions for a date · <strong>Dismiss</strong> keeps it out of the log
            but never deletes it
          </span>
        )}
      </div>

      {shown.length === 0 && (
        <div className="empty">
          <p className="muted">
            Nothing in the queue matches {AI_VERDICT_LABELS[verdict as AiVerdict]}.
          </p>
          <button className="link" onClick={() => setVerdict("")}>
            show everything
          </button>
        </div>
      )}

      <ul className="items">
        {shown.map((item) => (
          <li key={item.id} className={selected.has(item.id) ? "item selected" : "item"}>
            <input
              type="checkbox"
              checked={selected.has(item.id)}
              onChange={() => toggle(item.id)}
              aria-label={`Select ${item.title}`}
            />
            <div className="body">
              <div className="meta">
                <span className="pill">{item.regulator}</span>
                <span className="muted">{item.category}</span>
                <span className="muted">{formatDate(item.published_at)}</span>
                <span className="muted small">{item.source_name}</span>
              </div>
              <h3>
                <ItemLink url={item.url}>{item.title}</ItemLink>
              </h3>
              {item.ai_summary ? (
                <p className="summary">
                  <span
                    className="ai-badge"
                    title={`Drafted by ${item.ai_model || "the assistant"}. A suggestion — nothing is applied until you apply it.`}
                  >
                    AI
                  </span>
                  {item.ai_summary}
                  {item.summary !== item.ai_summary && (
                    <button
                      className="link use-summary"
                      title="Copy this into the Summary column. Until you do, the log and the Excel export keep the publisher's own wording."
                      onClick={() => useSummary(item)}
                    >
                      use this
                    </button>
                  )}
                </p>
              ) : (
                item.summary && <p className="summary">{item.summary}</p>
              )}

              {(() => {
                const suggested = clientSuggestions(item).filter(
                  (s) => !item.clients.includes(s.name),
                );
                const confident = suggested.filter((s) => s.verdict === "yes");
                if (!suggested.length) return null;
                return (
                  <div className="suggested">
                    <span className="muted small">Suggested:</span>
                    {suggested.map((s) => (
                      <button
                        key={s.name}
                        className={`chip suggest ${s.verdict}`}
                        title={s.why || "No reason recorded."}
                        onClick={() => tagClient(item, s.name)}
                      >
                        {s.name}
                        {s.verdict === "maybe" ? " ?" : ""}
                      </button>
                    ))}
                    {confident.length > 1 && (
                      <button
                        className="link"
                        onClick={() => acceptSuggested(item, confident.map((s) => s.name))}
                      >
                        accept all {confident.length}
                      </button>
                    )}
                    {/* The reasons, written out on request. They were only ever in a tooltip,
                        and a phone has no hover: "hover for why" was an instruction she could
                        not follow there. */}
                    <details className="why">
                      <summary className="small">why?</summary>
                      <ul className="small">
                        {suggested.map((s) => (
                          <li key={s.name}>
                            <strong>{s.name}</strong>
                            {s.verdict === "maybe" ? " (maybe)" : ""}: {s.why || "No reason recorded."}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                );
              })()}

              {/* Suggested tags she has not taken: one click accepts one. Her own are the
                  chips below, and the two are never the same list — ai_tags is what the
                  model proposed, item.tags is what she keeps. */}
              {(() => {
                const hers = item.tags ?? [];
                const offered = byName(
                  (item.ai_tags ?? []).filter((tag) => !hers.includes(tag)),
                );
                if (!offered.length) return null;
                return (
                  <div className="ai-tags">
                    <span className="label">Suggested:</span>
                    {offered.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="tag suggested"
                        title="Add this tag"
                        onClick={() => stage(item, { tags: [...hers, tag] })}
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                );
              })()}

              {/* Hers. Clicking one removes it; the picker below adds any other. */}
              {(item.tags ?? []).length > 0 && (
                <div className="own-tags">
                  {byName(item.tags ?? []).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="tag on"
                      title="Remove this tag"
                      onClick={() =>
                        stage(item, { tags: (item.tags ?? []).filter((t) => t !== tag) })
                      }
                    >
                      {tag} ×
                    </button>
                  ))}
                </div>
              )}

              {vocabulary.length > 0 && (
                <select
                  className="tag-picker"
                  value=""
                  aria-label={`Add a tag to ${item.title}`}
                  onChange={(event) => {
                    const tag = event.target.value;
                    if (tag) stage(item, { tags: [...(item.tags ?? []), tag] });
                  }}
                >
                  <option value="">Add a tag…</option>
                  {byName(vocabulary)
                    .filter((tag) => !(item.tags ?? []).includes(tag))
                    .map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                </select>
              )}

              <div className="tags">
                {clientNames.map((name) => (
                  <button
                    key={name}
                    className={
                      (
                        name === ALL_CLIENTS_TAG
                          ? coversLive(item.clients, live)
                          : item.clients.includes(name)
                      )
                        ? "chip on"
                        : "chip"
                    }
                    onClick={() => tagClient(item, name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
            <div className="actions">
              {/* Staged edits are only in the browser until she decides, so say so rather
                  than letting her assume the old save-as-you-go behaviour. */}
              {pending[item.id] && (
                <span className="staged" title="Saved when you press Track or Dismiss">
                  Not saved yet
                </span>
              )}
              <button
                className="primary"
                disabled={busy}
                title="Add it to the log — it appears in the Log view, the Excel export and the monthly board paper"
                onClick={() => decide([item.id], "tracked")}
              >
                Track
              </button>
              <button
                className="track-action"
                disabled={busy}
                title="Track it and add it to Actions, so it is waiting for you with a date to put against it"
                onClick={() => trackWithAction(item)}
              >
                Track + action
              </button>
              <button
                disabled={busy}
                title="Not one to track. It leaves the queue and stays out of the log, but the record is kept and stays searchable — nothing is ever deleted"
                onClick={() => decide([item.id], "dismissed")}
              >
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
